/**
 * DeviceAccessPanel — cómo se llega a un dispositivo (servidor, firewall,
 * router, switch, cabina, AP, impresora…).
 *
 * Importante: aquí NO se guardan credenciales. Solo el método (iLO/IPMI, SSH,
 * RDP, Terminal Server, web de gestión…), el host/puerto y el camino (VPN,
 * jump host, consola). Usuario y contraseña viven en el gestor de secretos.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const FALLBACK_TYPES = [
  { value: 'ipmi', label: 'IPMI / iLO / iDRAC', default_port: 443 },
  { value: 'ssh', label: 'SSH', default_port: 22 },
  { value: 'rdp', label: 'RDP (Escritorio remoto)', default_port: 3389 },
  { value: 'terminal_server', label: 'Terminal Server / RDS', default_port: 3389 },
  { value: 'https', label: 'Web de gestión (HTTPS)', default_port: 443 },
  { value: 'http', label: 'Web de gestión (HTTP)', default_port: 80 },
  { value: 'console', label: 'Consola serie / out-of-band', default_port: null },
  { value: 'vnc', label: 'VNC', default_port: 5900 },
  { value: 'telnet', label: 'Telnet', default_port: 23 },
  { value: 'vpn', label: 'VPN', default_port: 443 },
  { value: 'other', label: 'Otro', default_port: null },
];

const ACCESS_ICONS = {
  ipmi: '🔧',
  ssh: '🔒',
  rdp: '🖥️',
  terminal_server: '🖥️',
  https: '🌐',
  http: '🌐',
  console: '⌨️',
  vnc: '🖥️',
  telnet: '🔌',
  vpn: '🔐',
  other: '🔗',
};

// Tipos en los que el destino se puede abrir en el navegador.
const WEB_TYPES = new Set(['ipmi', 'https', 'http', 'vpn']);

const EMPTY_FORM = {
  name: '',
  access_type: 'ipmi',
  host: '',
  port: '',
  via: '',
  is_primary: false,
  notes: '',
};

function defaultPortFor(type, meta) {
  const entry = (meta.access_types || FALLBACK_TYPES).find(t => t.value === type);
  return entry?.default_port ?? null;
}

function typeLabel(type, meta) {
  const entry = (meta.access_types || FALLBACK_TYPES).find(t => t.value === type);
  return entry?.label || type;
}

function destinationLabel(ap) {
  if (!ap.host) return '—';
  return ap.port ? `${ap.host}:${ap.port}` : ap.host;
}

function destinationUrl(ap) {
  if (!ap.host || !WEB_TYPES.has(ap.access_type)) return null;
  const scheme = ap.access_type === 'http' ? 'http' : 'https';
  return `${scheme}://${ap.host}${ap.port ? `:${ap.port}` : ''}`;
}

// L6 Passwords: el acceso (host/puerto/vía) se queda en CMDB; el secreto vive
// en el vault. Estos enlaces solo abren la app con el recurso ya identificado.
const PASSWORDS_URL = (process.env.REACT_APP_PASSWORDS_URL || 'http://localhost:5179').replace(/\/$/, '');

function passwordsLinkUrl(ap, elementName) {
  const label = `${ap.name}${elementName ? ` · ${elementName}` : ''}`;
  const meta = {
    accessType: ap.access_type,
    accessTypeLabel: ap.access_type_label || ap.access_type,
    host: ap.host || null,
    port: ap.port ?? null,
    protocol: ap.protocol || null,
    via: ap.via || null,
  };
  const params = new URLSearchParams();
  params.set('link', `cmdb:ci_access_point:${ap.id}`);
  params.set('label', label);
  params.set('meta', JSON.stringify(meta));
  return `${PASSWORDS_URL}/passwords/vault?${params.toString()}`;
}

function passwordsResourceUrl(ap) {
  const params = new URLSearchParams();
  params.set('resource', `cmdb:ci_access_point:${ap.id}`);
  return `${PASSWORDS_URL}/passwords/vault?${params.toString()}`;
}

export default function DeviceAccessPanel({
  objectType,
  objectId,
  companyId,
  elementName,
  defaultHost = '',
  onCountChange,
  canEdit = true,
}) {
  const [points, setPoints] = useState([]);
  const [meta, setMeta] = useState({ access_types: FALLBACK_TYPES });
  const [credStatus, setCredStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    if (!objectId || !companyId) {
      setPoints([]);
      onCountChange?.(0);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [pointsRes, metaRes] = await Promise.all([
        api.getCiAccessPoints(objectType, objectId, companyId),
        api.getCiAccessPointsMeta(companyId).catch(() => ({})),
      ]);
      const list = pointsRes.access_points || [];
      setPoints(list);
      onCountChange?.(list.length);
      if (metaRes?.access_types?.length) setMeta(metaRes);
      // Insignia de credenciales: solo recuentos, nunca el secreto.
      if (list.length) {
        api.getCredentialStatus(
          list.map((ap) => `ci_access_point:${ap.id}`),
          companyId
        ).then(setCredStatus).catch(() => setCredStatus({}));
      } else {
        setCredStatus({});
      }
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los accesos');
    } finally {
      setLoading(false);
    }
  }, [objectType, objectId, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, host: defaultHost || '' });
    setEditing(null);
    setShowForm(false);
  };

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, host: defaultHost || '', port: defaultPortFor('ipmi', meta) || '' });
    setEditing(null);
    setError('');
    setShowForm(true);
  };

  const openEdit = (ap) => {
    setForm({
      name: ap.name || '',
      access_type: ap.access_type || 'ssh',
      host: ap.host || '',
      port: ap.port ?? '',
      via: ap.via || '',
      is_primary: Boolean(ap.is_primary),
      notes: ap.notes || '',
    });
    setEditing(ap);
    setError('');
    setShowForm(true);
  };

  const patch = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleTypeChange = (value) => {
    setForm(prev => {
      const next = { ...prev, access_type: value };
      // Si el puerto estaba vacío o era el del tipo anterior, proponemos el nuevo.
      const previousDefault = defaultPortFor(prev.access_type, meta);
      if (!prev.port || String(prev.port) === String(previousDefault ?? '')) {
        next.port = defaultPortFor(value, meta) ?? '';
      }
      // Si el nombre estaba vacío, ponemos algo reconocible.
      if (!prev.name.trim()) {
        next.name = typeLabel(value, meta);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio.'); return; }
    if (!form.host.trim()) { setError('Indica la IP o el host de conexión.'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        object_type: objectType,
        object_id: objectId,
        name: form.name.trim(),
        access_type: form.access_type,
        host: form.host.trim(),
        port: form.port === '' ? null : Number(form.port),
        via: form.via.trim() || null,
        is_primary: Boolean(form.is_primary),
        notes: form.notes.trim() || null,
      };
      if (editing) {
        await api.updateCiAccessPoint(editing.id, payload, companyId);
      } else {
        await api.createCiAccessPoint(payload, companyId);
      }
      resetForm();
      load();
    } catch (err) {
      setError(err.message || 'No se pudo guardar el acceso');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ap) => {
    if (!window.confirm(`¿Eliminar el acceso "${ap.name}" de ${elementName || 'este elemento'}?`)) return;
    setError('');
    try {
      await api.deleteCiAccessPoint(ap.id, companyId);
      load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el acceso');
    }
  };

  if (!objectId) {
    return (
      <div className="dv-empty-state">
        <span style={{ fontSize: '1.5rem' }}>🔗</span>
        <p style={{ marginTop: 8 }}>Guarda el elemento primero</p>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          Después podrás documentar cómo se llega a él (iLO, SSH, Terminal Server…).
        </p>
      </div>
    );
  }

  const accessTypes = meta.access_types?.length ? meta.access_types : FALLBACK_TYPES;
  const primaryCount = points.filter(p => p.is_primary).length;

  return (
    <div>
      <div className="dv-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <div>
            <div className="dv-section-title" style={{ margin: 0, border: 'none', paddingBottom: 0 }}>
              Cómo se llega a {elementName || 'este equipo'}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3 }}>
              iLO/IPMI, SSH, RDP, Terminal Server, web de gestión… Sin credenciales:
              usuario y contraseña se guardan en el gestor de secretos.
            </div>
          </div>
          {canEdit && !showForm && (
            <button
              type="button"
              className="dv-btn dv-btn-primary"
              onClick={openCreate}
              style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
            >
              <PlusIcon style={{ width: 14, height: 14 }} />
              Añadir acceso
            </button>
          )}
        </div>
        {points.length > 0 && (
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
            {points.length} acceso{points.length !== 1 ? 's' : ''}
            {primaryCount > 0 ? ` · ${primaryCount} principal${primaryCount !== 1 ? 'es' : ''}` : ''}
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-danger py-2" style={{ fontSize: '0.82rem' }} role="alert">
          ⚠ {error}
        </div>
      )}

      {loading && (
        <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: '0.85rem' }}>Cargando accesos…</div>
      )}

      {showForm && !loading && (
        <div style={{
          background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 8,
          padding: 16, marginBottom: 16,
        }}>
          <div className="dv-section-title" style={{ margin: '0 0 12px 0', border: 'none', padding: 0, fontSize: '0.95rem' }}>
            {editing ? 'Editar acceso' : 'Nuevo acceso'}
          </div>
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Nombre *</label>
              <input
                className="form-control form-control-sm"
                value={form.name}
                onChange={e => patch('name', e.target.value)}
                placeholder="iLO, SSH, Terminal Server…"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Tipo de acceso</label>
              <select
                className="form-select form-select-sm"
                value={form.access_type}
                onChange={e => handleTypeChange(e.target.value)}
              >
                {accessTypes.map(t => (
                  <option key={t.value} value={t.value}>
                    {ACCESS_ICONS[t.value] || '🔗'} {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>IP / host *</label>
              <input
                className="form-control form-control-sm"
                value={form.host}
                onChange={e => patch('host', e.target.value)}
                placeholder="10.0.0.20 / srv01.empresa.local"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Puerto</label>
              <input
                type="number"
                className="form-control form-control-sm"
                value={form.port}
                onChange={e => patch('port', e.target.value)}
                placeholder="22"
              />
            </div>
            <div className="col-md-5">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Cómo se llega (opcional)</label>
              <input
                className="form-control form-control-sm"
                value={form.via}
                onChange={e => patch('via', e.target.value)}
                placeholder="VPN administración, jump host, Terminal Server TS01…"
              />
            </div>
            <div className="col-md-7">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Notas</label>
              <input
                className="form-control form-control-sm"
                value={form.notes}
                onChange={e => patch('notes', e.target.value)}
                placeholder="Detalles del acceso (sin contraseñas)…"
              />
            </div>
            <div className="col-md-4" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 24 }}>
              <label style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={form.is_primary}
                  onChange={e => patch('is_primary', e.target.checked)}
                />
                Acceso principal
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              className="dv-btn dv-btn-primary"
              onClick={handleSave}
              disabled={saving}
              style={{ fontSize: '0.78rem' }}
            >
              {saving ? 'Guardando…' : (editing ? 'Actualizar' : 'Crear acceso')}
            </button>
            <button
              type="button"
              className="dv-btn dv-btn-secondary"
              onClick={resetForm}
              style={{ fontSize: '0.78rem' }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {!loading && points.length === 0 && !showForm && (
        <div className="dv-empty-state">
          <span style={{ fontSize: '1.5rem' }}>🔗</span>
          <p style={{ marginTop: 8 }}>Sin accesos definidos</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
            Añade cómo se llega a este equipo: iLO/IPMI, SSH, RDP, Terminal Server…
          </p>
        </div>
      )}

      {!loading && points.length > 0 && (
        <table className="dv-servers-table">
          <thead>
            <tr>
              <th>Acceso</th>
              <th>Tipo</th>
              <th>Destino</th>
              <th>Cómo se llega</th>
              <th>Credencial</th>
              <th style={{ textAlign: 'center' }}>Principal</th>
              {canEdit && <th style={{ textAlign: 'right' }}>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {points.map(ap => {
              const url = destinationUrl(ap);
              return (
                <tr key={ap.id}>
                  <td style={{ fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>{ACCESS_ICONS[ap.access_type] || '🔗'}</span>
                      <span>{ap.name}</span>
                    </div>
                    {ap.notes && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        {ap.notes}
                      </div>
                    )}
                  </td>
                  <td>{ap.access_type_label || ap.access_type}</td>
                  <td>
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--accent)', textDecoration: 'none' }}
                      >
                        {destinationLabel(ap)}
                        <ArrowTopRightOnSquareIcon style={{ width: 12, height: 12 }} />
                      </a>
                    ) : (
                      <span style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                        {destinationLabel(ap)}
                      </span>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>{ap.via || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const status = credStatus[`ci_access_point:${ap.id}`];
                      if (!status || !status.count) return null;
                      const tipos = (status.accessTypes || []).join(', ');
                      return (
                        <a
                          href={passwordsResourceUrl(ap)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`${status.count} credencial(es) en L6 Passwords${tipos ? ` (${tipos})` : ''}`}
                          style={{
                            display: 'inline-block',
                            marginRight: 10,
                            padding: '1px 7px',
                            borderRadius: 10,
                            fontSize: '0.74rem',
                            fontWeight: 600,
                            background: 'rgba(34,197,94,0.14)',
                            color: '#15803d',
                            textDecoration: 'none',
                          }}
                        >
                          🔑 {status.count}{status.hasPrimary ? ' ★' : ''}
                        </a>
                      );
                    })()}
                    {canEdit && (
                      <a
                        href={passwordsLinkUrl(ap, elementName)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Vincular una credencial de L6 Passwords a este acceso"
                        style={{ color: 'var(--accent)', textDecoration: 'none', marginRight: 10, fontWeight: 600 }}
                      >
                        {credStatus[`ci_access_point:${ap.id}`]?.count ? 'Añadir' : '🔑 Vincular'}
                      </a>
                    )}
                    <a
                      href={passwordsResourceUrl(ap)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Ver las credenciales vinculadas a este acceso en L6 Passwords"
                      style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                    >
                      Ver
                    </a>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {ap.is_primary ? <span title="Acceso principal">★</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  {canEdit && (
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={() => openEdit(ap)}
                        title="Editar"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 4 }}
                      >
                        <PencilSquareIcon style={{ width: 16, height: 16 }} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(ap)}
                        title="Eliminar"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 4 }}
                      >
                        <TrashIcon style={{ width: 16, height: 16 }} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
